/**
 * Unified AI Prompt Sentences
 * Each sentence serves as BOTH the Mad Libs display AND the Fal.ai prompt.
 *
 * After filling slots, the prompt becomes:
 * "Use Image 1 as a base image for the format, Only edit the black area. " + filled_sentence
 *
 * Syntax: ${slotId} marks a fillable blank
 * Slot IDs: style-1, mood-1, text-1, noun-1, noun-2, elements-1
 */

import type { DisplaySentence, MadLibsSlot } from '@/types';

// ============================================
// Shared slot definitions
// ============================================

const STYLE_SLOT: MadLibsSlot = {
  id: 'style-1',
  category: 'style',
  placeholder: 'art style',
  required: true,
};

const MOOD_SLOT: MadLibsSlot = {
  id: 'mood-1',
  category: 'mood',
  placeholder: 'mood',
  required: true,
};

const TEXT_SLOT: MadLibsSlot = {
  id: 'text-1',
  category: 'text',
  placeholder: 'your text',
  required: false,
};

const ELEMENTS_SLOT: MadLibsSlot = {
  id: 'elements-1',
  category: 'elements',
  placeholder: 'decorative touches',
  required: false,
};

// Helper: create a noun slot with defaults and options
function nounSlot(
  id: string,
  placeholder: string,
  defaultValue: string,
  options: readonly string[]
): MadLibsSlot {
  return { id, category: 'noun', placeholder, required: false, defaultValue, options };
}

// Helper: sentence with all 4 slots
function fullSentence(sentence: string): DisplaySentence {
  return {
    sentence,
    slots: [STYLE_SLOT, MOOD_SLOT, TEXT_SLOT, ELEMENTS_SLOT],
  };
}

// Helper: sentence with style + mood only (no text/elements)
function coreSentence(sentence: string): DisplaySentence {
  return {
    sentence,
    slots: [STYLE_SLOT, MOOD_SLOT],
  };
}

// Helper: sentence with style + mood + text (no elements)
function textSentence(sentence: string): DisplaySentence {
  return {
    sentence,
    slots: [STYLE_SLOT, MOOD_SLOT, TEXT_SLOT],
  };
}

// Helper: sentence with style + mood + noun slots (no elements)
function nounCoreSentence(sentence: string, nouns: MadLibsSlot[]): DisplaySentence {
  return {
    sentence,
    slots: [STYLE_SLOT, MOOD_SLOT, ...nouns],
  };
}

// Helper: sentence with style + mood + text + noun slots (no elements)
function nounTextSentence(sentence: string, nouns: MadLibsSlot[]): DisplaySentence {
  return {
    sentence,
    slots: [STYLE_SLOT, MOOD_SLOT, TEXT_SLOT, ...nouns],
  };
}

// ============================================
// Display sentences by templateId -> layoutId
// ============================================

const LAYOUT_SENTENCES: Record<string, Record<string, DisplaySentence>> = {
  // ---- PET DOG TAG ----
  'pet-dog-tag': {
    'classic-portrait': nounTextSentence(
      "Pet portrait from Image 2 centered in upper portion, adorned with ${noun-1}. ${style-1} style, ${mood-1} mood. Display pet name and contact info: '${text-1}' - exact spelling, no other text",
      [
        nounSlot('noun-1', 'decorative accent', 'paw prints', [
          'paw prints',
          'bone border',
          'heart accents',
          'collar tag',
          'stars',
        ]),
      ]
    ),
    'full-portrait': nounTextSentence(
      "Pet portrait from Image 2 fills the entire tag, ${noun-1} accent near name at bottom. ${style-1} style, ${mood-1} mood. Display pet name: '${text-1}' - exact spelling, no other text",
      [
        nounSlot('noun-1', 'bottom accent', 'heart', [
          'heart',
          'paw prints',
          'stars',
          'floral wreath',
          'banner ribbon',
        ]),
      ]
    ),
    'paw-print': nounTextSentence(
      "${noun-1} from Image 2 centered. ${style-1} style, ${mood-1} mood. Display pet name and contact info: '${text-1}' - exact spelling, no other text",
      [
        nounSlot('noun-1', 'print style', 'paw print', [
          'paw print',
          'paw with heart pad',
          'double paw prints',
          'paw trail',
          'paw and bone',
        ]),
      ]
    ),
    decorative: nounTextSentence(
      "Decorative pet-themed design adorned with ${noun-1}. ${style-1} style, ${mood-1} mood. Display pet name and contact info: '${text-1}' - exact spelling, no other text",
      [
        nounSlot('noun-1', 'theme element', 'mountains and trees', [
          'mountains and trees',
          'sun and moon',
          'flowers and butterflies',
          'ocean waves',
          'stars and clouds',
        ]),
      ]
    ),
  },

  // ---- MILITARY MEMORIAL ----
  'military-memorial': {
    'classic-portrait': nounTextSentence(
      "Portrait from Image 2 centered in upper portion, framed with ${noun-1}. ${style-1} style, ${mood-1} mood. Display name, rank and dates: '${text-1}' - exact spelling, no other text",
      [
        nounSlot('noun-1', 'memorial accent', 'dog tags', [
          'dog tags',
          'wreath',
          'cross',
          'stars',
          'folded flag',
        ]),
      ]
    ),
    'portrait-with-flag': nounTextSentence(
      "Portrait from Image 2 in upper portion with American flag behind, ${noun-1} accent. ${style-1} style, ${mood-1} mood. Display name, rank and dates: '${text-1}' - exact spelling, no other text",
      [
        nounSlot('noun-1', 'patriotic symbol', 'eagle', [
          'eagle',
          'stars and stripes',
          'wreath',
          'crossed rifles',
          'purple heart',
        ]),
      ]
    ),
  },

  // ---- BUSINESS CARD ----
  'business-card': {
    'logo-centered': nounTextSentence(
      "Logo from Image 2 centered in upper portion, with ${noun-1}. ${style-1} style, ${mood-1} mood. Display name and title: '${text-1}' - exact spelling, no other text",
      [
        nounSlot('noun-1', 'accent detail', 'line separator', [
          'line separator',
          'corner accent',
          'geometric border',
          'subtle pattern',
          'double line frame',
        ]),
      ]
    ),
    'logo-left-info-right': nounTextSentence(
      "Logo from Image 2 on left side, ${noun-1} between. ${style-1} style, ${mood-1} mood. Display name and title: '${text-1}' - exact spelling, no other text",
      [
        nounSlot('noun-1', 'divider style', 'vertical line separator', [
          'vertical line separator',
          'decorative divider',
          'dot pattern divider',
          'minimal rule line',
          'ornamental border',
        ]),
      ]
    ),
    'minimal-text': nounTextSentence(
      "Clean business card with ${noun-1}, professional typography. ${style-1} style, ${mood-1} mood. Display name and title: '${text-1}' - exact spelling, no other text",
      [
        nounSlot('noun-1', 'subtle accent', 'thin line separator', [
          'thin line separator',
          'small monogram',
          'corner dots',
          'hairline border',
        ]),
      ]
    ),
    'decorative-border': nounTextSentence(
      "Ornate ${noun-1} framing the card. ${style-1} style, ${mood-1} mood. Display name and title: '${text-1}' - exact spelling, no other text",
      [
        nounSlot('noun-1', 'border style', 'ornate flourishes', [
          'ornate flourishes',
          'art deco corners',
          'vine border',
          'geometric frame',
          'filigree border',
        ]),
      ]
    ),
  },

  // ---- WALLET CARD ----
  'wallet-card': {
    monogram: nounTextSentence(
      "Large centered monogram with ${noun-1}. ${style-1} style, ${mood-1} mood. Display initials: '${text-1}' - exact spelling, no other text",
      [
        nounSlot('noun-1', 'border style', 'decorative border frame', [
          'decorative border frame',
          'filigree frame',
          'laurel wreath border',
          'geometric border',
        ]),
      ]
    ),
    'initials-and-pattern': nounTextSentence(
      "Bold initials centered with ${noun-1} filling the background. ${style-1} style, ${mood-1} mood. Display initials: '${text-1}' - exact spelling, no other text",
      [
        nounSlot('noun-1', 'pattern type', 'repeating geometric pattern', [
          'repeating geometric pattern',
          'floral pattern',
          'damask pattern',
          'art deco pattern',
        ]),
      ]
    ),
    'logo-centered': textSentence(
      "Logo from Image 2 centered on the surface. ${style-1} style, ${mood-1} mood. Display name: '${text-1}' - exact spelling, no other text"
    ),
    'signature-style': textSentence(
      "Elegant stylized signature text centered across the surface. ${style-1} style, ${mood-1} mood. Display signature name: '${text-1}' - exact spelling, no other text"
    ),
  },

  // ---- METAL ART ----
  'metal-art': {
    'geometric-pattern': nounCoreSentence(
      'Intricate ${noun-1} filling the entire surface. ${style-1} style, ${mood-1} mood',
      [
        nounSlot('noun-1', 'pattern subject', 'geometric pattern', [
          'geometric pattern',
          'mandala',
          'sacred geometry',
          'tessellation',
          'celtic knot',
        ]),
      ]
    ),
    'nature-scene': nounTextSentence(
      "Beautiful ${noun-1} filling the entire surface. ${style-1} style, ${mood-1} mood. Display text: '${text-1}' - exact spelling, no other text",
      [
        nounSlot('noun-1', 'scene subject', 'mountain landscape', [
          'mountain landscape',
          'ocean waves',
          'forest scene',
          'desert sunset',
          'night sky',
        ]),
      ]
    ),
    'skull-and-roses': nounCoreSentence(
      'Bold ${noun-1} design centered with surrounding details filling the surface. ${style-1} style, ${mood-1} mood',
      [
        nounSlot('noun-1', 'main subject', 'skull and roses', [
          'skull and roses',
          'dragon',
          'wolf',
          'eagle',
          'lion',
        ]),
      ]
    ),
  },

  // ---- PLANT LABEL ----
  'plant-label': {
    'botanical-illustration': fullSentence(
      "Plant illustration from Image 2 in upper portion, adorned with ${elements-1}. ${style-1} style, ${mood-1} mood. Display plant name: '${text-1}' - exact spelling, no other text"
    ),
    'simple-name': textSentence(
      "Clean plant label with small leaf accents. ${style-1} style, ${mood-1} mood. Display plant name: '${text-1}' - exact spelling, no other text"
    ),
    'scientific-style': textSentence(
      "Scientific botanical study with plant from Image 2. ${style-1} style, ${mood-1} mood. Display plant name with scientific name: '${text-1}' - exact spelling, no other text"
    ),
    'decorative-border': fullSentence(
      "Ornate garden-themed border framing the label, adorned with ${elements-1}. ${style-1} style, ${mood-1} mood. Display plant name: '${text-1}' - exact spelling, no other text"
    ),
  },

  // ---- PORTRAIT ----
  portrait: {
    'classic-oval': textSentence(
      "Portrait from Image 2 in oval frame centered in upper portion. ${style-1} style, ${mood-1} mood. Display memorial text: '${text-1}' - exact spelling, no other text"
    ),
    'full-frame': textSentence(
      "Portrait from Image 2 fills the upper portion. ${style-1} style, ${mood-1} mood. Display memorial text: '${text-1}' - exact spelling, no other text"
    ),
    'with-dates': textSentence(
      "Portrait from Image 2 centered with dates framing the portrait. ${style-1} style, ${mood-1} mood. Display name and dates: '${text-1}' - exact spelling, no other text"
    ),
    'decorative-memorial': nounTextSentence(
      "Decorative memorial design with ${noun-1}, adorned with ${noun-2}. ${style-1} style, ${mood-1} mood. Display memorial text: '${text-1}' - exact spelling, no other text",
      [
        nounSlot('noun-1', 'memorial subject', 'doves', [
          'doves',
          'angels',
          'cross',
          'praying hands',
          'candles',
        ]),
        nounSlot('noun-2', 'decorative accent', 'roses', [
          'roses',
          'lilies',
          'vines',
          'laurel wreath',
        ]),
      ]
    ),
  },

  // ---- EMBLEM ----
  emblem: {
    'shield-shape': fullSentence(
      "Traditional shield-shaped crest with emblem from Image 2 centered inside, adorned with ${elements-1}. ${style-1} style, ${mood-1} mood. Display family name and year: '${text-1}' - exact spelling, no other text"
    ),
    'circular-seal': fullSentence(
      "Round seal design with emblem from Image 2 centered inside, adorned with ${elements-1}. ${style-1} style, ${mood-1} mood. Display family name and year: '${text-1}' - exact spelling, no other text"
    ),
    heraldic: nounTextSentence(
      "Full coat of arms design with ${noun-1}. ${style-1} style, ${mood-1} mood. Display family name and year: '${text-1}' - exact spelling, no other text",
      [
        nounSlot('noun-1', 'central element', 'shield', [
          'shield',
          'eagle',
          'lion',
          'crossed swords',
          'crown',
        ]),
      ]
    ),
    'avatar-focus': textSentence(
      "Subject from Image 2 fills the upper portion. ${style-1} style, ${mood-1} mood. Display text: '${text-1}' below the subject - exact spelling, no other text"
    ),
    'badge-style': fullSentence(
      "Achievement badge or emblem design with text centered inside, adorned with ${elements-1}. ${style-1} style, ${mood-1} mood. Display text: '${text-1}' - exact spelling, no other text"
    ),
    'graffiti-style': textSentence(
      "Street art graffiti style lettering with decorative elements. ${style-1} style, ${mood-1} mood. Display text: '${text-1}' - exact spelling, no other text"
    ),
    'banner-style': fullSentence(
      "Decorative ribbon banner with ornamental elements, adorned with ${elements-1}. ${style-1} style, ${mood-1} mood. Display name: '${text-1}' - exact spelling, no other text"
    ),
  },

  // ---- LUGGAGE TAG ----
  'luggage-tag': {
    'globe-and-name': nounTextSentence(
      "${noun-1} in upper portion. ${style-1} style, ${mood-1} mood. Display owner info: '${text-1}' - exact spelling, no other text",
      [
        nounSlot('noun-1', 'map illustration', 'world map', [
          'world map',
          'globe illustration',
          'compass and map',
          'atlas pages',
          'flight path map',
        ]),
      ]
    ),
    'passport-stamp': nounTextSentence(
      "Vintage ${noun-1} collage with name centered in a stamp frame. ${style-1} style, ${mood-1} mood. Display owner info: '${text-1}' - exact spelling, no other text",
      [
        nounSlot('noun-1', 'stamp style', 'passport stamp', [
          'passport stamp',
          'travel visa stamps',
          'postmark stamps',
          'airport stamps',
          'customs stamps',
        ]),
      ]
    ),
    'compass-rose': nounTextSentence(
      "Ornate ${noun-1} centered, name on banner below. ${style-1} style, ${mood-1} mood. Display owner info: '${text-1}' - exact spelling, no other text",
      [
        nounSlot('noun-1', 'navigation symbol', 'compass rose', [
          'compass rose',
          'anchor',
          'sextant',
          'ship wheel',
          'lighthouse',
        ]),
      ]
    ),
    'city-skyline': nounTextSentence(
      "${noun-1} across the top. ${style-1} style, ${mood-1} mood. Display owner info: '${text-1}' - exact spelling, no other text",
      [
        nounSlot('noun-1', 'skyline subject', 'city skyline', [
          'city skyline',
          'mountain range',
          'coastal harbor',
          'desert landscape',
          'forest treeline',
        ]),
      ]
    ),
  },

  // ---- QR CODE ----
  'qr-code': {
    'centered-decorated': fullSentence(
      "Decorative frame around QR code area - DO NOT fill the center square. ${elements-1} accents. ${style-1} style, ${mood-1} mood. Display label: '${text-1}' - exact spelling, no other text"
    ),
    'centered-minimal': textSentence(
      "Clean border around QR code area, DO NOT fill the QR code area. ${style-1} style, ${mood-1} mood. Display label: '${text-1}' - exact spelling, no other text"
    ),
    'bottom-with-header': textSentence(
      "Decorative header design in upper portion, QR code area below - DO NOT fill the bottom area. ${style-1} style, ${mood-1} mood. Display header text: '${text-1}' - exact spelling, no other text"
    ),
    'bottom-minimal': textSentence(
      "Clean border around QR code area, DO NOT fill the QR code area. ${style-1} style, ${mood-1} mood. Display label: '${text-1}' - exact spelling, no other text"
    ),
  },

  // ---- CUSTOM PROMPT ----
  'custom-prompt': {
    'dragon-shield': coreSentence(
      'A fierce dragon wrapped around a shield, flames surrounding it. ${style-1} style, ${mood-1} mood'
    ),
    'celtic-tree': coreSentence(
      'Ornate Celtic knot border with a tree of life in center. ${style-1} style, ${mood-1} mood'
    ),
  },
};

// ============================================
// Template-level fallback sentences
// ============================================

const TEMPLATE_FALLBACKS: Record<string, DisplaySentence> = {
  'pet-dog-tag': nounTextSentence(
    "Pet-themed design with name and contact info, adorned with ${noun-1}. ${style-1} style, ${mood-1} mood. Display pet info: '${text-1}' - exact spelling, no other text",
    [
      nounSlot('noun-1', 'decorative accent', 'paw prints', [
        'paw prints',
        'bones',
        'hearts',
        'stars',
      ]),
    ]
  ),
  'military-memorial': nounTextSentence(
    "Military memorial tribute with name, rank and dates, framed with ${noun-1}. ${style-1} style, ${mood-1} mood. Display memorial info: '${text-1}' - exact spelling, no other text",
    [nounSlot('noun-1', 'memorial symbol', 'dog tags', ['dog tags', 'eagle', 'cross', 'wreath'])]
  ),
  'business-card': nounTextSentence(
    "Professional business card with name and title, with ${noun-1}. ${style-1} style, ${mood-1} mood. Display business info: '${text-1}' - exact spelling, no other text",
    [
      nounSlot('noun-1', 'accent detail', 'line separator', [
        'line separator',
        'corner accent',
        'decorative border',
        'monogram',
      ]),
    ]
  ),
  'wallet-card': nounTextSentence(
    "Personal wallet card design with ${noun-1}. ${style-1} style, ${mood-1} mood. Display text: '${text-1}' - exact spelling, no other text",
    [
      nounSlot('noun-1', 'design subject', 'decorative pattern', [
        'decorative pattern',
        'monogram',
        'geometric border',
        'floral motif',
      ]),
    ]
  ),
  'metal-art': nounCoreSentence(
    'Artistic ${noun-1} filling the entire surface. ${style-1} style, ${mood-1} mood',
    [
      nounSlot('noun-1', 'art subject', 'design', [
        'geometric pattern',
        'nature scene',
        'abstract art',
        'tribal design',
      ]),
    ]
  ),
  'plant-label': textSentence(
    "Plant label with botanical details. ${style-1} style, ${mood-1} mood. Display plant name: '${text-1}' - exact spelling, no other text"
  ),
  portrait: textSentence(
    "Portrait-based design. ${style-1} style, ${mood-1} mood. Display memorial text: '${text-1}' - exact spelling, no other text"
  ),
  emblem: fullSentence(
    "Bold emblem or crest design, adorned with ${elements-1}. ${style-1} style, ${mood-1} mood. Display text: '${text-1}' - exact spelling, no other text"
  ),
  'luggage-tag': nounTextSentence(
    "Travel-themed ${noun-1} design. ${style-1} style, ${mood-1} mood. Display owner info: '${text-1}' - exact spelling, no other text",
    [
      nounSlot('noun-1', 'travel subject', 'luggage tag', [
        'luggage tag',
        'travel badge',
        'journey marker',
        'adventure tag',
      ]),
    ]
  ),
  'qr-code': textSentence(
    "Design with protected QR code area - DO NOT fill the QR area. ${style-1} style, ${mood-1} mood. Display label: '${text-1}' - exact spelling, no other text"
  ),
  'custom-prompt': coreSentence('Custom design as described. ${style-1} style, ${mood-1} mood'),
};

// ============================================
// Public API
// ============================================

/**
 * Get the display sentence for a given template and optional layout.
 * Returns layout-specific sentence if available, falls back to template-level, then null.
 */
export function getDisplaySentence(
  templateId: string,
  layoutId?: string | null
): DisplaySentence | null {
  // Try layout-specific first
  if (layoutId) {
    const layoutSentences = LAYOUT_SENTENCES[templateId];
    if (layoutSentences) {
      const sentence = layoutSentences[layoutId];
      if (sentence) return sentence;
    }
  }

  // Fall back to template-level
  return TEMPLATE_FALLBACKS[templateId] ?? null;
}
